import React, { Fragment, useEffect, useState, useCallback } from 'react';
import { Box, Text } from 'grommet';
import { startCase } from 'lodash';
import { useDispatch } from 'react-redux';

import { APPLICABLE_FILTERS } from '../components/reports/filter-selector';
import { authorizedFetch } from '../utils';
import { addError } from '../toasts/toasts.actions';

export const ReportFilterString = ({ filters, excludedFilters }) => {
  const [mappedFilter, setMappedFilter] = useState();
  const [mappedExcludedFilter, setMappedExcludedFilter] = useState();
  const dispatch = useDispatch();

  const processFilters = useCallback(
    async (filterList, keysToGet) => {
      const aborter = new AbortController();
      let res;
      try {
        if (keysToGet.length > 0) {
          res = await authorizedFetch(
            `/api/search/eventflow-*/_search`,
            'POST',
            {
              _source: ['id', 'name'],
              query: {
                terms: {
                  'id.keyword': keysToGet
                }
              }
            },
            aborter.signal
          );
        }

        const categories = filterList.reduce((acc, filter) => {
          // 1. try to associate filter to config object
          let filterItem = APPLICABLE_FILTERS.find(
            filterItem => filterItem.field === filter.field
          );

          // if we didnt find a matching filter in the config,
          // apply per-case logic
          if (!filterItem) {
            if (filter.field === 'properties.flowId') {
              filterItem = {
                category: 'flow',
                name: 'Flow'
              };
            } else if (filter.field.includes('properties.url.queryParams')) {
              const queryParamParts = filter.field.split('.');
              filterItem = {
                name: startCase(queryParamParts[queryParamParts.length - 1]),
                category: 'query_param'
              };
            } else if (filter.field.includes('properties.vars')) {
              const varsParts = filter.field.split('.');
              filterItem = {
                name: startCase(varsParts[varsParts.length - 1]),
                category: 'page_variable'
              };
            } else if (filter.field.includes('properties.userAttrs')) {
              const attrsParts = filter.field.split('.');
              filterItem = {
                name: startCase(attrsParts[attrsParts.length - 1]),
                category: 'user_attr'
              };
            }
          }

          // initialize if doesnt exist
          if (!acc[filterItem.category]) {
            acc[filterItem.category] = {
              fields: {}
            };
          }

          if (!acc[filterItem.category].fields[filterItem.name]) {
            acc[filterItem.category].fields[filterItem.name] = [];
          }
          acc[filterItem.category].fields[filterItem.name].push({
            ...filter,
            // forgive me as i have sinned here...
            // get name from filterItem => name from elastic res => default to filter key
            name:
              filterItem?.input?.options?.find(opt => opt.key === filter.key)
                ?.displayName ||
              res?.hits?.hits?.find(
                hit => hit._source.id === filter.key && hit._source?.name
              )?._source?.name ||
              filter.key
          });

          return acc;
        }, {});

        return categories;
      } catch (e) {
        if (e.name !== 'AbortError') {
          dispatch(
            addError('Whoops! Something went wrong building the filter string')
          );
        }
        return {};
      }
    },
    [dispatch]
  );

  useEffect(() => {
    // Reset state immediately when filters change to show updates right away
    if (!filters?.length && !excludedFilters?.length) {
      setMappedFilter(null);
      setMappedExcludedFilter(null);
      return;
    }

    const v4Regex = new RegExp(
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
    );

    const includedKeysToGet = [];
    const excludedKeysToGet = [];

    if (filters?.length) {
      filters.forEach(filter => {
        const { key } = filter;
        if (v4Regex.test(key)) {
          includedKeysToGet.push(key);
        }
      });
    }

    if (excludedFilters?.length) {
      excludedFilters.forEach(filter => {
        const { key } = filter;
        if (v4Regex.test(key)) {
          excludedKeysToGet.push(key);
        }
      });
    }

    const allKeysToGet = [
      ...new Set([...includedKeysToGet, ...excludedKeysToGet])
    ];

    async function mapAllFilters() {
      const [includedCategories, excludedCategories] = await Promise.all([
        filters?.length
          ? processFilters(filters, allKeysToGet)
          : Promise.resolve(null),
        excludedFilters?.length
          ? processFilters(excludedFilters, allKeysToGet)
          : Promise.resolve(null)
      ]);

      setMappedFilter(includedCategories);
      setMappedExcludedFilter(excludedCategories);
    }

    mapAllFilters();
  }, [filters, excludedFilters, processFilters]);

  const renderFilterString = (mappedFilterData, isExcluded = false) => {
    if (!mappedFilterData || Object.keys(mappedFilterData).length === 0) {
      return null;
    }

    return (
      <Text as='p' margin='none'>
        <Text weight='bold'>
          {isExcluded ? 'Excluded Filters = ' : 'Applied Filters = '}
        </Text>
        {/* iterate through categories of selected filters */}
        {Object.keys(mappedFilterData).map((filterCategory, idx) => (
          <Fragment key={filterCategory}>
            {/*
              display the category, append AND if not the first category.
              campaign
            */}
            <Text weight='bold'>
              {idx === 0
                ? `${startCase(filterCategory)}: `
                : ` AND ${startCase(filterCategory)}: `}
            </Text>

            {/*
              iterate through named fields inside of the category
              campaign -> campaign.fields -> campaign.fields.advertiser
            */}
            {Object.keys(mappedFilterData[filterCategory].fields).map(
              (filterField, idx) => (
                <span key={filterField}>
                  {' '}
                  {idx >= 1 && 'AND '}
                  {filterField} (
                  {/*
                    iterate through the filters applied to a certain field in a category
                    campaign -> campaign.fields -> campaign.fields.advertiser -> [a,b,c]
                   */}
                  {mappedFilterData[filterCategory].fields[filterField].map(
                    (filterValue, idx) => {
                      return (
                        <span key={filterValue.name}>
                          {idx === 0
                            ? `${filterValue.name}`
                            : ` OR ${filterValue.name}`}
                        </span>
                      );
                    }
                  )}
                  )
                </span>
              )
            )}
          </Fragment>
        ))}
      </Text>
    );
  };

  const hasIncludedFilters =
    mappedFilter && Object.keys(mappedFilter).length > 0;
  const hasExcludedFilters =
    mappedExcludedFilter && Object.keys(mappedExcludedFilter).length > 0;
  const hasExcludedFiltersPending =
    excludedFilters?.length > 0 && !hasExcludedFilters;

  return (
    <Box pad='small' width='80%' direction='column' gap='small'>
      {hasIncludedFilters ? (
        renderFilterString(mappedFilter, false)
      ) : (
        <Text weight='bold'>Applied Filters = None</Text>
      )}
      {hasExcludedFilters ? (
        renderFilterString(mappedExcludedFilter, true)
      ) : hasExcludedFiltersPending ? (
        <Text weight='bold'>Excluded Filters = None</Text>
      ) : null}
    </Box>
  );
};
